import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import axiosInstance from '@/lib/axios'
import { type DashboardConfig, ROLE_DEFAULTS } from '@/types/dashboard'

const lsKey = (userId: number) => `dashboard_config_${userId}`

const getDefault = (ruolo: string | null | undefined): DashboardConfig =>
  ROLE_DEFAULTS[ruolo ?? ''] ?? ROLE_DEFAULTS['ADMIN'] ?? { widgets: [], links: [] }

export function useDashboardConfig() {
  const { user } = useAuth()
  const [config, setConfig] = useState<DashboardConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!user) return

    // Instant display: try localStorage first
    const cached = localStorage.getItem(lsKey(user.id))
    if (cached) {
      try {
        setConfig(JSON.parse(cached))
        setIsLoading(false)
      } catch { /* ignore corrupt cache */ }
    }

    // Authoritative: fetch from DB
    axiosInstance.get('/users/me')
      .then(res => {
        const raw: string | null = res.data.dashboard_config ?? null
        if (raw) {
          try {
            const parsed = JSON.parse(raw) as DashboardConfig
            setConfig(parsed)
            localStorage.setItem(lsKey(user.id), raw)
          } catch {
            setConfig(getDefault(user.ruolo))
          }
        } else {
          setConfig(getDefault(user.ruolo))
        }
        setIsLoading(false)
      })
      .catch(() => {
        if (!config) setConfig(getDefault(user.ruolo))
        setIsLoading(false)
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const saveConfig = useCallback(async (newConfig: DashboardConfig) => {
    if (!user) return
    setConfig(newConfig)
    const json = JSON.stringify(newConfig)
    localStorage.setItem(lsKey(user.id), json)
    await axiosInstance.put('/users/me/dashboard-config', { dashboard_config: json })
  }, [user])

  const resetToDefault = useCallback(async () => {
    if (!user) return
    const def = getDefault(user.ruolo)
    await saveConfig(def)
  }, [user, saveConfig])

  const resolved = config ?? getDefault(user?.ruolo)

  return { config: resolved, saveConfig, resetToDefault, isLoading }
}
